import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

import { Card } from './Card';

export interface HeroCardProps {
  /** Мелкая подпись сверху: «СБОР 2 · ПАДЕЛ», «НАБОР · ВОЛЕЙБОЛ» */
  eyebrow: ReactNode;
  /** Крупная строка, ради которой открыли экран: дата сбора, «3 из 8», цена */
  title: ReactNode;
  /** Строки под заголовком: площадка, район, очередь */
  meta?: ReactNode;
  /** Значок состояния справа сверху */
  badge?: ReactNode;
  /**
   * Где значок: `top` — в строке подписи, заголовок под ним на всю ширину (по умолчанию);
   * `side` — колонкой рядом с заголовком, для крупного аватара в профиле.
   */
  badgePlacement?: 'top' | 'side';
  /** Доля заполнения 0…1 — полоса под заголовком */
  progress?: number | null;
  /** Полоса набранного минимума окрашена в успех */
  progressDone?: boolean;
  /** Карточка ведёт внутрь: появляется стрелка, вся шапка становится целью нажатия */
  onOpen?: () => void;
  /** Выделенная рамка: то, что происходит прямо сейчас */
  accent?: boolean;
  children?: ReactNode;
}

function Chevron() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="text-muted shrink-0"
    >
      <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Главный блок экрана в одном виде на всех экранах: подпись, крупная строка, детали, состояние.
 *
 * Так выглядит «Ближайший сбор» на главной, и остальные экраны собирались каждый по-своему —
 * человек заново разбирался, где здесь главное. Шапка кликабельна, если экран ведёт внутрь;
 * действия (оплата, RSVP) кладутся в `children` и остаются вне цели нажатия, чтобы промах по
 * кнопке не уводил с экрана.
 */
export function HeroCard({
  eyebrow,
  title,
  meta,
  badge,
  badgePlacement = 'top',
  progress,
  progressDone,
  onOpen,
  accent,
  children,
}: HeroCardProps) {
  const eyebrowEl = (
    <div className="text-muted text-xs font-semibold tracking-wide uppercase">{eyebrow}</div>
  );
  const body = (
    <>
      <div className="num mt-1 text-[26px] leading-tight font-bold tracking-tight">{title}</div>
      {meta ? <div className="text-muted mt-1 flex flex-col gap-0.5 text-sm">{meta}</div> : null}
    </>
  );
  // Ревью 2026-09-24: значок стоял колонкой рядом со всем блоком и отнимал до 38 % ширины у
  // заголовка — дата сбора ломалась в три строки на 320 px. Теперь он делит строку только с
  // подписью, заголовок и детали идут на всю ширину.
  const head =
    badgePlacement === 'side' ? (
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {eyebrowEl}
          {body}
        </div>
        <div className="flex shrink-0 items-start gap-2">
          {badge}
          {onOpen ? <Chevron /> : null}
        </div>
      </div>
    ) : (
      <div>
        <div className="flex min-h-6 items-center justify-between gap-3">
          <div className="min-w-0 flex-1">{eyebrowEl}</div>
          {badge || onOpen ? (
            <div className="flex shrink-0 items-center gap-2">
              {badge}
              {onOpen ? <Chevron /> : null}
            </div>
          ) : null}
        </div>
        {body}
      </div>
    );

  return (
    <Card accent={accent}>
      {onOpen ? (
        <div
          role="button"
          tabIndex={0}
          onClick={onOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen();
            }
          }}
          className="pressable -mx-2 cursor-pointer rounded-lg px-2 py-1"
        >
          {head}
        </div>
      ) : (
        head
      )}

      {typeof progress === 'number' ? (
        <div className="bg-surface-2 mt-3 h-2 w-full overflow-hidden rounded-full" aria-hidden="true">
          <div
            className={cn('h-full rounded-full', progressDone ? 'bg-success' : 'bg-accent')}
            style={{ width: `${Math.min(100, Math.max(0, Math.round(progress * 100)))}%` }}
          />
        </div>
      ) : null}

      {children}
    </Card>
  );
}
